/**
 * AddPatientMethodDialog (T08) — the "Add new patient" method-choice modal.
 *
 * Per tech_design_spec §6.1 (D6): clicking "Add patient" first asks the PS how
 * they want to create the patient:
 *   - "Search in INSi" (recommended) → opens InsiSearchModal (T06).
 *   - "Create manually"             → opens the existing CreatePatientDialog,
 *                                      patient saved PROVISOIRE.
 *
 * The INSi option is only offered when `useInsiAccess()` is true; otherwise the
 * choice is meaningless and the caller goes straight to manual create (the
 * caller is responsible for that short-circuit — this dialog still defends by
 * only rendering the INSi card when access is granted).
 *
 * Visuals follow the Figma spec: vertical full-width option cards (icon-left,
 * text-middle, radio-right), light-blue selected state, exact copy. All copy is
 * routed through `t()`.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogActions,
  Box as MuiBox,
  Typography,
  Button,
} from '@mui/material';

import {
  Search as SearchIcon,
  DescriptionOutlined as ManualIcon,
  ArrowRightAlt as ArrowRightAltIcon,
} from '@mui/icons-material';
import DialogTitle from 'components/Dialogs/DialogTitle';

import { useInsiAccess } from 'services/insi';

export default function AddPatientMethodDialog({ open, onClose, onSelect }) {
  const { t } = useTranslation();
  const hasInsiAccess = useInsiAccess();
  // INSi is the recommended default when available.
  const [method, setMethod] = useState(hasInsiAccess ? 'insi' : 'manual');

  const handleContinue = () => {
    onSelect(method);
  };

  const renderOption = ({ value, icon, title, description }) => {
    const selected = method === value;
    return (
      <SOptionCard
        type="button"
        data-id={`add-patient-method-${value}`}
        selected={selected}
        onClick={() => setMethod(value)}
      >
        <SIconBox>{icon}</SIconBox>
        <SOptionText>
          <SOptionTitle>{title}</SOptionTitle>
          <SOptionDescription>{description}</SOptionDescription>
        </SOptionText>
        <SRadio selected={selected} aria-hidden>
          {selected && <SRadioDot />}
        </SRadio>
      </SOptionCard>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="add-patient-method-dialog-title"
      PaperProps={{ style: { width: 720, maxWidth: 720, borderRadius: 8 } }}
    >
      <DialogTitle id="add-patient-method-dialog-title" onClose={onClose}>
        <Typography variant="h4">{t('Add new patient')}</Typography>
      </DialogTitle>

      <DialogContent>
        <SHint>{t('How would you like to add this patient?')}</SHint>

        <SOptions>
          {hasInsiAccess &&
            renderOption({
              value: 'insi',
              icon: <SearchIcon />,
              title: t('Search in INSi'),
              description: t(
                "Retrieve the patient's verified identity from the national registry. Recommended for certified data exchange."
              ),
            })}

          {renderOption({
            value: 'manual',
            icon: <ManualIcon />,
            title: t('Create manually'),
            description: t(
              'Fill in patient details yourself. The patient will be saved as Provisional until an INSi search is performed.'
            ),
          })}
        </SOptions>
      </DialogContent>

      <SFooter>
        <SCancelButton onClick={onClose} disableElevation data-id="add-patient-cancel">
          {t('Cancel')}
        </SCancelButton>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          data-id="add-patient-continue"
          onClick={handleContinue}
          endIcon={<ArrowRightAltIcon />}
        >
          {t('Continue')}
        </Button>
      </SFooter>
    </Dialog>
  );
}

AddPatientMethodDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const SHint = styled(Typography)`
  && {
    margin-bottom: 16px;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.palette.colors.black100};
  }
`;

const SOptions = styled(MuiBox)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SOptionCard = styled.button`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
  gap: 16px;
  padding: 16px;
  cursor: pointer;
  background-color: ${({ theme, selected }) =>
    selected ? theme.palette.colors.blue200 : theme.palette.colors.white};
  border: 1px solid
    ${({ theme, selected }) =>
      selected ? theme.palette.colors.blue100 : theme.palette.colors.gray200};
  border-radius: 8px;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  :hover {
    border-color: ${({ theme }) => theme.palette.colors.blue100};
  }
`;

const SIconBox = styled.span`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(33, 100, 232, 0.04);
  color: ${({ theme }) => theme.palette.colors.blue100};
`;

const SOptionText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SOptionTitle = styled.span`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const SOptionDescription = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  color: ${({ theme }) => theme.palette.colors.gray400};
`;

const SRadio = styled.span`
  flex: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.8px solid
    ${({ theme, selected }) =>
      selected ? theme.palette.colors.blue100 : theme.palette.colors.gray300};
`;

const SRadioDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.colors.blue100};
`;

const SFooter = styled(DialogActions)`
  && {
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 30px 30px;
    border-top: 1px solid ${({ theme }) => theme.palette.colors.gray200};
  }
`;

const SCancelButton = styled(Button)`
  && {
    background: #f7f7f7;
    color: ${({ theme }) => theme.palette.text.primary};
    border-radius: 6px;
    text-transform: none;
    font-weight: 600;

    :hover {
      background: ${({ theme }) => theme.palette.colors.gray200};
    }
  }
`;
